const configuredApiUrl = import.meta.env.VITE_API_URL?.trim();
const inferredProductionApiOrigin =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'euchub.app' || window.location.hostname === 'www.euchub.app')
    ? 'https://api.euchub.app'
    : '';
const API_ORIGIN = configuredApiUrl || inferredProductionApiOrigin || '';
const API_BASE = API_ORIGIN
  ? API_ORIGIN.endsWith('/api')
    ? API_ORIGIN
    : `${API_ORIGIN}/api`
  : '/api';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ─── Token getter (wired by AuthContext via Clerk) ────────────────────────────

type TokenGetter = () => Promise<string | null>;
let _getToken: TokenGetter | null = null;
let _tokenOverride: string | null = null;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function setTokenGetter(fn: TokenGetter): void {
  _getToken = fn;
}

export function setAuthTokenOverride(token: string | null): void {
  _tokenOverride = token;
}

async function authHeaders(): Promise<HeadersInit> {
  if (_tokenOverride) {
    return { Authorization: `Bearer ${_tokenOverride}` };
  }

  if (!_getToken) return {};

  for (let attempt = 0; attempt < 5; attempt += 1) {
    const token = await _getToken();
    if (token) return { Authorization: `Bearer ${token}` };
    await sleep(150 * (attempt + 1));
  }

  return {};
}

// ─── Core request helpers ─────────────────────────────────────────────────────

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeaders()),
      ...options.headers,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    const msg = err?.data?.message ?? err?.message ?? `HTTP ${res.status}`;
    throw new ApiError(msg, res.status);
  }

  const text = await res.text();
  if (!text) return undefined as T;

  const json = JSON.parse(text);
  // Unwrap TransformInterceptor envelope { success, data }
  return (json?.data ?? json) as T;
}

async function requestForm<T>(path: string, body: FormData, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    method: options.method ?? 'POST',
    body,
    headers: {
      ...(await authHeaders()),
      ...options.headers,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    const msg = err?.data?.message ?? err?.message ?? `HTTP ${res.status}`;
    throw new ApiError(msg, res.status);
  }

  const text = await res.text();
  if (!text) return undefined as T;

  const json = JSON.parse(text);
  return (json?.data ?? json) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  postForm: <T>(path: string, body: FormData) => requestForm<T>(path, body),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

export function parseJobDescription(text: string) {
  return api.post<{ company: string; role: string; jobUrl: string | null; notes: string }>(
    '/ai/parse-jd',
    { text },
  );
}

export async function streamChat(
  message: string,
  history: Array<{ role: 'user' | 'assistant'; content: string }>,
  onChunk: (text: string) => void,
  onDone: () => void,
  onError: (err: string) => void,
  conversationId?: string,
  onConversationId?: (id: string) => void,
  jobContextId?: string,
  chatContext?: 'general' | 'algo' | 'system-design',
  interviewType?: string,
  problemSlug?: string,
  code?: string,
  language?: string,
  lessonId?: string,
) {
  const token = _getToken ? await _getToken() : null;

  const res = await fetch(`${API_BASE}/ai/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ message, history, stream: true, conversationId, jobContextId, chatContext, interviewType, problemSlug, code, language, lessonId }),
  });

  if (!res.ok || !res.body) {
    onError('Failed to connect to AI service');
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const data = line.slice(6).trim();
      if (data === '[DONE]') { onDone(); return; }
      try {
        const parsed = JSON.parse(data);
        if (parsed.conversationId) onConversationId?.(parsed.conversationId);
        if (parsed.content) onChunk(parsed.content);
        if (parsed.error) onError(parsed.error);
      } catch { /* ignore */ }
    }
  }
  onDone();
}

export interface Conversation {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

export interface ConversationDetail extends Conversation {
  messages: Array<{ id: string; role: 'user' | 'assistant'; content: string; createdAt: string }>;
}

export const conversationsApi = {
  list: () => api.get<Conversation[]>('/ai/conversations'),
  get: (id: string) => api.get<ConversationDetail>(`/ai/conversations/${id}`),
};
